import React from 'react'
import styles from './JobList.module.css'
import { JobListing } from './types'
import { YenIcon, MapPinIcon, StarIcon } from './Icons'
import {
    favoriteOffer,
    unfavoriteOffer,
} from '../../../app/api/favoriteOfferApi'
import { useFavoriteOffers } from './FavoriteOffersContext'

type JobCardProps = {
    job: JobListing
}

export default function JobCard({ job }: JobCardProps) {
    const offerId = job.offerId
    const { loaded, favoriteOfferIds } = useFavoriteOffers()
    const [isFavorite, setIsFavorite] = React.useState(false)
    const [isSubmitting, setIsSubmitting] = React.useState(false)

    // 初回の状態取得が完了したタイミングで一度だけ初期値を反映する。
    // syncedRef は「初期同期を済ませたか」を表し、初期同期時のほか
    // ユーザー操作時(handleFavoriteClick)にも立てる。これにより、
    // 読み込み完了前のクリックで入った楽観的トグルを、後から着地する
    // 初期同期が上書きしてしまう競合を防ぐ。
    const syncedRef = React.useRef(false)
    React.useEffect(() => {
        if (loaded && !syncedRef.current) {
            syncedRef.current = true
            setIsFavorite(favoriteOfferIds.has(offerId))
        }
    }, [loaded, favoriteOfferIds, offerId])

    const handleFavoriteClick = async () => {
        if (isSubmitting) return

        // ユーザー操作が入った以降は初期同期でこの楽観更新を上書きしない。
        // 初回 GET 未完了(loaded === false)のクリックでも、後から着地する
        // 初期同期が setIsFavorite を実行してユーザー操作を打ち消すのを防ぐ。
        syncedRef.current = true

        const next = !isFavorite
        setIsFavorite(next) // 楽観的に UI を更新
        setIsSubmitting(true)
        try {
            const res = next
                ? await favoriteOffer(offerId)
                : await unfavoriteOffer(offerId)
            if (res.status === 'not_logged_in') {
                window.location.href = '/login'
                return
            }
            if (res.status !== 'success') {
                setIsFavorite(!next) // 失敗時は元に戻す
            }
        } catch {
            setIsFavorite(!next) // 通信エラー時は元に戻す
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className={styles.jobCard} data-view-label={job.analyticsLabel}>
            <img
                src={job.image}
                alt={job.title}
                className={styles.jobCardPhoto}
            />
            <div className={styles.jobCardBody}>
                <p className={styles.jobCardBadge}>{job.badgeLabel}</p>
                <p className={styles.jobCardTitle}>{job.title}</p>
                <div className={styles.jobCardCompany}>
                    <img
                        src={job.brandLogo}
                        alt={job.brandName}
                        className={styles.jobCardCompanyLogo}
                    />
                    <span className={styles.jobCardCompanyName}>
                        {job.brandName}
                    </span>
                </div>
                <div className={styles.jobCardDivider} />
                <div className={styles.jobCardMeta}>
                    <div className={styles.jobCardMetaItem}>
                        <YenIcon className={styles.jobCardMetaIcon} size={22} />
                        <span className={styles.jobCardSalary}>
                            <span className={styles.jobCardMetaLabel}>
                                {job.salaryUnit}
                            </span>
                            <span className={styles.jobCardSalaryValue}>
                                {job.salary}
                            </span>
                            <span className={styles.jobCardMetaLabel}>万</span>
                        </span>
                    </div>
                    <div
                        className={`${styles.jobCardMetaItem} ${styles.jobCardMetaItemLocation}`}
                    >
                        <MapPinIcon
                            className={styles.jobCardMetaIcon}
                            size={22}
                        />
                        <span className={styles.jobCardLocation}>
                            <span className={styles.jobCardPrefecture}>
                                {job.prefecture}
                            </span>
                            <span className={styles.jobCardCity}>
                                {job.city}
                            </span>
                        </span>
                    </div>
                </div>
                <div className={styles.jobCardActions}>
                    <button
                        type="button"
                        onClick={handleFavoriteClick}
                        disabled={isSubmitting}
                        aria-pressed={isFavorite}
                        className={[
                            styles.jobCardBtnOutline,
                            isFavorite ? styles.jobCardBtnOutlineActive : '',
                        ]
                            .filter(Boolean)
                            .join(' ')}
                        data-click-label={`${job.analyticsLabel}-気になる`}
                    >
                        <StarIcon className={styles.jobCardBtnIcon} />
                        気になる
                    </button>
                    <a
                        href={job.href}
                        className={styles.jobCardBtnFilled}
                        data-click-label={`${job.analyticsLabel}-詳細を見る`}
                    >
                        詳細を見る
                    </a>
                </div>
            </div>
        </div>
    )
}
